import React, { useEffect, useRef, useState, useMemo } from "react";
import { Card, Dialog } from "@mui/material";
import { Line, Bar } from "react-chartjs-2";

// Style Sheet File
import SoftTypography from "../SoftTypography";
import SoftBox from "../SoftBox";
import configs from "./configs";
import ExpandableView from "./ExpandableView";

// Icons — same set the desktop PerformBox uses, keyed by widget title.
import topKPI from "../../img/widgets/top_kpi.png";
import adminBulletin from "../../img/widgets/admin_bulletin.png";
import companyValues from "../../img/widgets/company_values.png";
import level from "../../img/widgets/level_widgets.png";
import personalGoals from "../../img/widgets/personal_goals.png";
import playersMemo from "../../img/widgets/players_memo.png";
import bonusEstimate from "../../img/widgets/bonus_estimate.png";
import ranking from "../../img/widgets/ranking_widgets.png";

// Original PerformBox with minor adjustments for mobile
function PerformBoxMobile({ isTopKPI, title, amount, showGraph = true, showCharts = true }) {
  const widgetIcon = isTopKPI ? topKPI : {
    'Admin Bulletin': adminBulletin,
    'Company Values': companyValues,
    'Level': level,
    'My Level': level,
    'Personal Goals': personalGoals,
    'Player Memo': playersMemo,
    'Bonus Estimate': bonusEstimate,
    'Ranking': ranking
  }[title];
  const chartRef = useRef(null);
  const [chartData, setChartData] = useState({});
  const [modal, setModal] = useState(false);
  const { data, options } = chartData;

  const closeModal = () => {
    setModal(false);
  };

  useEffect(() => {
    const kpiChartData = {
      labels: ["Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
      datasets: [
        {
          label: title,
          color: "dark",
          data: [50, 45, 60, 60, 80, 65, 90, 80, 100],
        },
      ],
    };

    const chartDatasets = kpiChartData.datasets.map((dataset) => ({
      ...dataset,
      tension: 0.4,
      pointRadius: 0,
      borderWidth: 2,
      borderColor: "#ffffff",
      fill: false,
      maxBarThickness: 6,
    }));

    setChartData(configs(kpiChartData.labels, chartDatasets));
  }, []);

  return (
    <>
      <Dialog
        open={modal}
        onClose={closeModal}
        sx={{
          // Dim scrim behind the glass modal (light).
          "& .MuiBackdrop-root": {
            backgroundColor: "rgba(15,23,42,0.16)",
          },
          ".MuiPaper-root": {
            padding: { mini: "1rem", md: "1rem 1.5rem" },
            borderRadius: "24px",
            // Light frosted-glass paper.
            background:
              "linear-gradient(180deg, rgba(255,255,255,0.70) 0%, rgba(255,255,255,0.60) 100%)",
            boxShadow:
              "0px 24px 48px -8px rgba(16,24,40,0.18), inset 0px 1px 0px 0px rgba(255,255,255,0.7)",
            backdropFilter: "blur(18px) saturate(160%)",
            WebkitBackdropFilter: "blur(18px) saturate(160%)",
            width: "100%",
            maxWidth: "600px",
            border: "1px solid rgba(255,255,255,0.6)",
          },
        }}
      >
        <ExpandableView title={title} amount={amount} closeModal={closeModal} />
      </Dialog>
      <Card
        onClick={() => setModal(true)}
        sx={{
          boxShadow: "0px 0px 0px 0px",
          background: "#F9FAFB",
          borderRadius: "6px",
          height: showCharts ? "7rem" : "84px",
          width: "100%",
          border: "0.72px solid #F2F4F7",
          justifyContent: "center",
          "&:hover": {
            cursor: "pointer",
          },
        }}
      >
        <SoftBox
          sx={{
            display: "flex",
            alignItems: "center",
          }}
        >
            <SoftBox
              sx={{
                "& img": {
                  height: "50px", width: "50px", marginLeft: "16px", padding: "8px",
                  backgroundColor: "#FFF",
                  border: "0.5px solid #F2F4F7",
                  borderRadius: "16px",
                  objectFit: "contain",
                }
              }}
            >
              <img src={widgetIcon} alt={title} />
            </SoftBox>

          <SoftBox lineHeight={1} sx={{ padding: { mini: 1, xxs: 2 }, width: "100%" }}>
            <SoftTypography
              variant="button"
              textTransform="capitalize"
              sx={{
                color: "#98A2B3",
                fontFamily: "DM Sans",
                fontSize: { mini: "12px", xxs: "12px", md: "16px" },
                fontWeight: 500
              }}
            >
              {title}
            </SoftTypography>

            <SoftTypography
              variant="h1"
              fontWeight="regular"
              sx={{
                color: "#000",
                whiteSpace: "nowrap",
                width: { mini: "110px", md: "290px" },
                overflow: "hidden",
                textOverflow: "ellipsis",
                fontFamily: "DM Sans",
                fontSize: { mini: "20px", xxs: "20px", md: "22px" },
                fontWeight: 500,
                ...(!showCharts && ({ width: { mini: "80%", xs: "90%", md: "100%" } }))
              }}
            >
              {amount}&nbsp;
            </SoftTypography>
          </SoftBox>
        </SoftBox>
        {useMemo(
          () => (
            (showCharts)
              ? <SoftBox
                  ref={chartRef}
                  sx={{
                    height: "7rem",
                    position: "absolute",
                    bottom: { mini: "-35px", xxs: "-45px", md: "-20px" },
                    right: { mini: "5px", xxs: "10px", md: 0 },
                    width: "60%",
                  }}
                >
                  {/* eslint-disable */}
                  {showGraph ? (
                    title === "Level" ? (
                      <Bar data={data} options={options} />
                    ) : (
                      <Line data={data} options={options} />
                    )
                  ) : null}
                </SoftBox>
              : null

          ),
          [chartData],
        )}
      </Card>
    </>
  );
}

export default PerformBoxMobile;
